import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import { ScopeAndSequenceLevel } from '@/common/types/scopeAndSequence';
import {
  getCumulativeGraphemes,
  getGraphemeById,
  getPhaseById,
} from '@/common/utils/scopeAndSequence';
import { Chip } from '@mui/material';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@shadcn/components/select';
import { useState } from 'react';
import styled from 'styled-components';

interface BulkLevelSelectProps {
  onLevelChange: (level: number) => void;
  initialLevel?: number;
}

export default function BulkLevelSelect({
  onLevelChange,
  initialLevel = 1,
}: BulkLevelSelectProps) {
  const levelsByPhase = scopeAndSequence.levels.reduce((acc, level) => {
    const phase = getPhaseById(level.phase);
    if (!phase) {
      return acc;
    }
    acc[phase.phase] = acc[phase.phase] || [];
    acc[phase.phase].push(level as ScopeAndSequenceLevel);
    return acc;
  }, {} as Record<number, ScopeAndSequenceLevel[]>);

  const [selectedLevel, setSelectedLevel] = useState<ScopeAndSequenceLevel>(
    (scopeAndSequence.levels.find((l) => l.level === initialLevel) ||
      scopeAndSequence.levels[0]) as ScopeAndSequenceLevel,
  );

  function getChipLabel(level: ScopeAndSequenceLevel) {
    if (level.newGraphemes.length === 0) {
      return 'All Previous';
    }

    const cumulativeGraphemes = getCumulativeGraphemes(level.level);
    if (cumulativeGraphemes.length > level.newGraphemes.length) {
      const additionalGraphemes: string[] = [];
      for (const graphemeId of level.newGraphemes) {
        const grapheme = getGraphemeById(graphemeId);
        if (grapheme) additionalGraphemes.push(grapheme.spelling);
      }
      return `All Previous + ${additionalGraphemes.join(', ')}`;
    }

    const newGraphemes: string[] = [];
    for (const graphemeId of level.newGraphemes) {
      const grapheme = getGraphemeById(graphemeId);
      if (grapheme) newGraphemes.push(grapheme.spelling);
    }
    return newGraphemes.join(', ');
  }

  function handleLevelChange(value: string) {
    const level = parseInt(value);
    const newSelectedLevel = scopeAndSequence.levels.find(
      (l) => l.level === level,
    ) as ScopeAndSequenceLevel;
    if (newSelectedLevel) {
      setSelectedLevel(newSelectedLevel);
      onLevelChange(level);
    }
  }

  return (
    <Select
      value={selectedLevel.level.toString()}
      onValueChange={handleLevelChange}
    >
      <SelectTrigger className="w-full" onClick={(e) => e.stopPropagation()}>
        <SelectValue>
          <LevelContainer>
            <LevelTriggerText>
              Level {selectedLevel.level} - {selectedLevel.description}
            </LevelTriggerText>
            <Chip
              label={getChipLabel(selectedLevel)}
              size="small"
              variant="outlined"
            />
          </LevelContainer>
        </SelectValue>
      </SelectTrigger>

      <StyledSelectContent onWheel={(e) => e.stopPropagation()}>
        {Object.entries(levelsByPhase).map(([phase, levels]) => (
          <SelectGroup key={phase}>
            <SelectLabel>Phase {phase}</SelectLabel>
            {levels.map((level: ScopeAndSequenceLevel) => (
              <SelectItem key={level.id} value={level.level.toString()}>
                <LevelContainer>
                  <LevelItemText>
                    {`Level ${level.level} - ${level.description}`}
                  </LevelItemText>
                  <Chip
                    label={getChipLabel(level)}
                    size="small"
                    variant="outlined"
                    style={{ marginLeft: 10 }}
                  />
                </LevelContainer>
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </StyledSelectContent>
    </Select>
  );
}

const StyledSelectContent = styled(SelectContent)`
  width: var(--radix-select-trigger-width);
`;

const LevelContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-right: 12px;
`;

const LevelTriggerText = styled.div`
  align-items: center;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @media (max-width: 600px) {
    font-size: 13px;
  }
`;

const LevelItemText = styled.div`
  align-items: center;
  font-size: 14px;
  overflow: visible;
  white-space: normal;
  @media (max-width: 600px) {
    font-size: 13px;
  }
`;
